import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Button, Form, Grid, Header, Icon, Input, Label, Message, Pagination, Segment, Select, Table} from 'semantic-ui-react';
import moment from 'moment';
import {toast} from 'react-toastify';
import {RastreamentoLog} from "../../../../models/rastreamento-log";
import GetPagedRastreamentoLogs from "../../../../services/backoffice/rastreamento-logs/get-rastreamento-logs";
import GetRastreamentoLogsExcel from "../../../../services/backoffice/rastreamento-logs/get-rastreamento-logs-excel";
import {RastreamentoLogFilter} from "../../../../filters/rastreamento-log-filter";
import StringUtils from "../../../../shared/utils/string-utils";
import {
    loadRastreamentoLogsCache,
    logSearchFingerprint,
    prepareLogSearchFilter,
    saveRastreamentoLogsCache,
} from "../../../../shared/utils/rastreamento-log-filter-utils";
import {useBlockUI} from "../../../../shared/services/block-ui-service";
import {ShortDto} from "../../../../models/short-dto";
import useClientService from "../../../../services/backoffice/hooks/useClientService";
import {ToasterConfig} from "../../../../config/ToasterConfig";

const MAX_PERIOD_DAYS = 31;
const MAX_RPM = 5000;
const DEFAULT_PER_PAGE = 25;

const DEFAULT_PERIOD_HOURS = 6;

function buildDefaultFilter(): RastreamentoLogFilter {
    return {
        SortField: 'SavedAt',
        SortType: 'Desc',
        Page: 1,
        PerPage: DEFAULT_PER_PAGE,
        InicioIntervalo: moment().subtract(DEFAULT_PERIOD_HOURS, 'hours').format('YYYY-MM-DDTHH:mm') as unknown as Date,
        FimIntervalo: moment().format('YYYY-MM-DDTHH:mm') as unknown as Date,
    };
}

function validatePeriod(filter: RastreamentoLogFilter): string | null {
    if (!filter.InicioIntervalo || !filter.FimIntervalo) {
        return 'Informe o período de início e fim.';
    }

    const inicio = moment(filter.InicioIntervalo);
    const fim = moment(filter.FimIntervalo);

    if (!inicio.isValid() || !fim.isValid()) {
        return 'Período inválido.';
    }

    if (fim.isBefore(inicio)) {
        return 'A data final deve ser posterior à data inicial.';
    }

    if (fim.diff(inicio, 'days', true) > MAX_PERIOD_DAYS) {
        return `O período máximo permitido é de ${MAX_PERIOD_DAYS} dias.`;
    }

    return null;
}

function validateRpmRange(filter: RastreamentoLogFilter): string | null {
    const {RpmMin, RpmMax} = filter;

    if (RpmMin != null && (RpmMin < 0 || RpmMin > MAX_RPM)) {
        return `RPM mínimo deve estar entre 0 e ${MAX_RPM}.`;
    }

    if (RpmMax != null && (RpmMax < 0 || RpmMax > MAX_RPM)) {
        return `RPM máximo deve estar entre 0 e ${MAX_RPM}.`;
    }

    if (RpmMin != null && RpmMax != null && RpmMin > RpmMax) {
        return 'RPM mínimo não pode ser maior que o RPM máximo.';
    }

    return null;
}

function validateFilters(filter: RastreamentoLogFilter): string | null {
    return validatePeriod(filter) ?? validateRpmRange(filter);
}

function validateClient(filter: RastreamentoLogFilter): string | null {
    if (!filter.clientId) {
        return 'Selecione um cliente antes de buscar.';
    }

    return null;
}

function formatCargaNoMotor(value: boolean | undefined): string {
    if (value === undefined || value === null) return '-';
    return value ? 'Sim' : 'Não';
}

const RastreamentoLogsListPage = () => {
    const [logs, setLogs] = useState<RastreamentoLog[]>([]);
    const [filter, setFilter] = useState<RastreamentoLogFilter>(buildDefaultFilter);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [totalCountLoaded, setTotalCountLoaded] = useState<boolean>(false);
    const [hasNextPage, setHasNextPage] = useState<boolean>(false);
    const [hasPreviousPage, setHasPreviousPage] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [exporting, setExporting] = useState<boolean>(false);
    const [showFilters, setShowFilters] = useState<boolean>(true);
    const [clientes, setClients] = useState<ShortDto[]>([]);
    const [hasSearched, setHasSearched] = useState<boolean>(false);
    const [searchVersion, setSearchVersion] = useState<number>(0);
    const [executedSearchFingerprint, setExecutedSearchFingerprint] = useState<string | null>(null);
    const {getAllClientsShortDto} = useClientService();
    const abortControllerRef = useRef<AbortController | null>(null);
    const skipNextFetchRef = useRef(false);
    const filterRef = useRef(filter);
    filterRef.current = filter;

    const blockUI = useBlockUI();

    useEffect(() => {
        blockUI?.setActive(loading || exporting);
    }, [loading, exporting, blockUI]);

    useEffect(() => {
        const cached = loadRastreamentoLogsCache();
        if (!cached) return;

        skipNextFetchRef.current = true;
        setFilter(cached.filter);
        setLogs(cached.data.itens);
        setTotalPages(cached.data.totalPages);
        setTotalCount(cached.data.count);
        setTotalCountLoaded(cached.data.totalCountAvailable !== false);
        setHasNextPage(cached.data.hasNextPage);
        setHasPreviousPage(cached.data.hasPreviousPage);
        setExecutedSearchFingerprint(logSearchFingerprint(cached.filter));
        setHasSearched(true);
    }, []);

    const listLogs = useCallback(async (searchFilter: RastreamentoLogFilter, options?: { includeTotal?: boolean }) => {
        const clientError = validateClient(searchFilter);
        if (clientError) {
            toast.warn(clientError, ToasterConfig.error);
            return;
        }

        const validationError = validateFilters(searchFilter);
        if (validationError) {
            toast.warn(validationError, ToasterConfig.error);
            return;
        }

        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const preparedFilter = prepareLogSearchFilter({
            ...searchFilter,
            IncludeTotal: options?.includeTotal ?? false,
        });

        setLoading(true);
        try {
            const pagedListResponse = await GetPagedRastreamentoLogs(preparedFilter, controller.signal);
            if (controller.signal.aborted) return;

            const countAvailable = pagedListResponse.totalCountAvailable !== false;
            setTotalCountLoaded(countAvailable);
            setTotalPages(countAvailable ? pagedListResponse.totalPages : 0);
            setTotalCount(countAvailable ? pagedListResponse.count : 0);
            setHasNextPage(pagedListResponse.hasNextPage);
            setHasPreviousPage(pagedListResponse.hasPreviousPage);
            setLogs(pagedListResponse.itens);
            setExecutedSearchFingerprint(logSearchFingerprint(preparedFilter));
            saveRastreamentoLogsCache(preparedFilter, pagedListResponse);
        } catch (error: any) {
            if (controller.signal.aborted) return;

            if (!error?.response) {
                const message = error?.message ?? 'Falha ao buscar logs de rastreamento.';
                toast.warn(message, ToasterConfig.error);
            }
        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        if (!hasSearched) return;
        if (skipNextFetchRef.current) {
            skipNextFetchRef.current = false;
            return;
        }
        listLogs(filterRef.current);
    }, [hasSearched, searchVersion, filter?.Page, listLogs]);

    useEffect(() => {
        getAllClientsShortDto()
            .then((response) => setClients(response))
            .catch(() => {});
    }, []);

    useEffect(() => () => abortControllerRef.current?.abort(), []);

    const triggerSearch = (nextFilter: RastreamentoLogFilter) => {
        const clientError = validateClient(nextFilter);
        if (clientError) {
            toast.warn(clientError, ToasterConfig.error);
            return;
        }

        setFilter(nextFilter);
        setHasSearched(true);
        setTotalCountLoaded(false);
        setSearchVersion(version => version + 1);
    };

    const handleLoadTotal = async () => {
        await listLogs({...filterRef.current, Page: 1}, {includeTotal: true});
    };

    const handleSearch = () => {
        triggerSearch({...filter, Page: 1});
    };

    const handleExportExcel = async () => {
        const clientError = validateClient(filter);
        if (clientError) {
            toast.warn(clientError, ToasterConfig.error);
            return;
        }

        const validationError = validateFilters(filter);
        if (validationError) {
            toast.warn(validationError, ToasterConfig.error);
            return;
        }

        setExporting(true);
        try {
            const blob = await GetRastreamentoLogsExcel(prepareLogSearchFilter(filter));
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `logs-rastreamento-${moment().format('YYYYMMDD-HHmm')}.xlsx`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            const message = error?.message
                ?? error?.response?.data?.errors?.[0]
                ?? error?.response?.data?.message
                ?? 'Falha ao exportar logs.';
            toast.warn(message, ToasterConfig.error);
        } finally {
            setExporting(false);
        }
    };

    const applyPreset = (hours: number) => {
        if (hours >= 24 * 7) {
            toast.info('Períodos longos podem demorar. Prefira filtros de motorista ou placa.', ToasterConfig.info);
        }
        triggerSearch({
            ...filter,
            InicioIntervalo: moment().subtract(hours, 'hours').format('YYYY-MM-DDTHH:mm') as unknown as Date,
            FimIntervalo: moment().format('YYYY-MM-DDTHH:mm') as unknown as Date,
            Page: 1,
        });
    };

    const applyTodayPreset = () => {
        triggerSearch({
            ...filter,
            InicioIntervalo: moment().startOf('day').format('YYYY-MM-DDTHH:mm') as unknown as Date,
            FimIntervalo: moment().format('YYYY-MM-DDTHH:mm') as unknown as Date,
            Page: 1,
        });
    };

    const parseRpmInput = (value: string): number | null => {
        if (value.trim() === '') return null;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const currentPage = Number(filter?.Page ?? 1);
    const perPage = Number(filter?.PerPage ?? DEFAULT_PER_PAGE);
    const rowOffset = (currentPage - 1) * perPage;
    const hasClientSelected = Boolean(filter.clientId);
    const isResultStale = hasSearched
        && executedSearchFingerprint !== null
        && logSearchFingerprint(filter) !== executedSearchFingerprint;

    return (
        <div>
            <Header as="h3" textAlign="center" color={'green'}>
                Logs de rastreamento dos veículos
            </Header>

            {!hasSearched && (
                <Message info>
                    Selecione o <strong>cliente</strong> e o <strong>período</strong> (padrão: últimas {DEFAULT_PERIOD_HOURS}h),
                    depois clique em <strong>Buscar</strong>. A coluna Data/Hora usa o momento de ingestão (<code>saved_at</code>).
                </Message>
            )}

            {isResultStale && (
                <Message warning size="small">
                    Filtros alterados. Clique em <strong>Buscar</strong> para atualizar os resultados antes de paginar ou exportar.
                </Message>
            )}

            <Segment basic style={{padding: 0}}>
                <Button onClick={() => setShowFilters(!showFilters)} primary>
                    <Icon name={showFilters ? 'hide' : 'unhide'}/>
                    {showFilters ? 'Ocultar filtros' : 'Exibir filtros'}
                </Button>
                <Button color="green" onClick={handleSearch} loading={loading} disabled={loading || exporting}>
                    <Icon name="search"/>
                    Buscar
                </Button>
                <Button color="teal" onClick={handleExportExcel} loading={exporting} disabled={loading || exporting || !hasSearched || isResultStale}>
                    <Icon name="download"/>
                    Exportar Excel
                </Button>
            </Segment>

            {showFilters && (
                <Segment>
                    <Form>
                        <Grid stackable doubling padded>
                            <Grid.Row verticalAlign="middle">
                                <Grid.Column width={16}>
                                    <Form.Field>
                                        <label>Atalhos de período</label>
                                        <Label.Group>
                                            <Label
                                                as="a"
                                                disabled={!hasClientSelected}
                                                onClick={() => hasClientSelected && applyPreset(24)}
                                            >
                                                Últimas 24h
                                            </Label>
                                            <Label
                                                as="a"
                                                disabled={!hasClientSelected}
                                                onClick={() => hasClientSelected && applyPreset(24 * 7)}
                                            >
                                                Últimos 7 dias
                                            </Label>
                                            <Label
                                                as="a"
                                                disabled={!hasClientSelected}
                                                onClick={() => hasClientSelected && applyTodayPreset()}
                                            >
                                                Hoje
                                            </Label>
                                        </Label.Group>
                                    </Form.Field>
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column computer={4} tablet={8} mobile={16}>
                                    <Form.Field>
                                        <label htmlFor="driverName">Nome do motorista</label>
                                        <Input
                                            fluid
                                            id="driverName"
                                            icon="user"
                                            placeholder="Busca parcial"
                                            value={filter?.MotoristaNome ?? ''}
                                            onChange={event =>
                                                setFilter(prev => ({...prev, MotoristaNome: event.target.value}))
                                            }
                                        />
                                    </Form.Field>
                                </Grid.Column>
                                <Grid.Column computer={4} tablet={8} mobile={16}>
                                    <Form.Field>
                                        <label htmlFor="cpf">CPF do motorista</label>
                                        <Input
                                            fluid
                                            id="cpf"
                                            icon="id card"
                                            placeholder="Parcial ou completo"
                                            value={filter?.MotoristaCpf ?? ''}
                                            onChange={event =>
                                                setFilter(prev => ({...prev, MotoristaCpf: event.target.value}))
                                            }
                                        />
                                    </Form.Field>
                                </Grid.Column>
                                <Grid.Column computer={4} tablet={8} mobile={16}>
                                    <Form.Field>
                                        <label htmlFor="placa">Placa</label>
                                        <Input
                                            fluid
                                            id="placa"
                                            icon="car"
                                            placeholder="Placa do veículo"
                                            value={filter?.Placa ?? ''}
                                            onChange={event =>
                                                setFilter(prev => ({...prev, Placa: event.target.value}))
                                            }
                                        />
                                    </Form.Field>
                                </Grid.Column>
                                <Grid.Column computer={4} tablet={8} mobile={16} required>
                                    <Form.Field required>
                                        <label htmlFor="cliente">Cliente</label>
                                        <Select
                                            fluid
                                            id="cliente"
                                            options={clientes}
                                            placeholder="Selecione um cliente"
                                            value={filter?.clientId || ''}
                                            search
                                            clearable
                                            onChange={(_e, data) => {
                                                setFilter(prev => ({
                                                    ...prev,
                                                    clientId: (data.value as string) || null,
                                                }));
                                            }}
                                        />
                                    </Form.Field>
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column computer={4} tablet={8} mobile={16}>
                                    <Form.Field>
                                        <label htmlFor="inicioIntervalo">Início do período</label>
                                        <Input
                                            fluid
                                            id="inicioIntervalo"
                                            type="datetime-local"
                                            value={filter?.InicioIntervalo?.toString() ?? ''}
                                            onChange={event =>
                                                setFilter(prev => ({
                                                    ...prev,
                                                    InicioIntervalo: event.target.value as unknown as Date,
                                                }))
                                            }
                                        />
                                    </Form.Field>
                                </Grid.Column>
                                <Grid.Column computer={4} tablet={8} mobile={16}>
                                    <Form.Field>
                                        <label htmlFor="fimIntervalo">Fim do período</label>
                                        <Input
                                            fluid
                                            id="fimIntervalo"
                                            type="datetime-local"
                                            value={filter?.FimIntervalo?.toString() ?? ''}
                                            onChange={event =>
                                                setFilter(prev => ({
                                                    ...prev,
                                                    FimIntervalo: event.target.value as unknown as Date,
                                                }))
                                            }
                                        />
                                    </Form.Field>
                                </Grid.Column>
                                <Grid.Column computer={4} tablet={8} mobile={16}>
                                    <Form.Field>
                                        <label htmlFor="rpmMin">RPM mínimo</label>
                                        <Input
                                            fluid
                                            id="rpmMin"
                                            type="number"
                                            min={0}
                                            max={MAX_RPM}
                                            placeholder={`0 a ${MAX_RPM}`}
                                            value={filter?.RpmMin ?? ''}
                                            onChange={event =>
                                                setFilter(prev => ({
                                                    ...prev,
                                                    RpmMin: parseRpmInput(event.target.value),
                                                }))
                                            }
                                        />
                                    </Form.Field>
                                </Grid.Column>
                                <Grid.Column computer={4} tablet={8} mobile={16}>
                                    <Form.Field>
                                        <label htmlFor="rpmMax">RPM máximo</label>
                                        <Input
                                            fluid
                                            id="rpmMax"
                                            type="number"
                                            min={0}
                                            max={MAX_RPM}
                                            placeholder={`0 a ${MAX_RPM}`}
                                            value={filter?.RpmMax ?? ''}
                                            onChange={event =>
                                                setFilter(prev => ({
                                                    ...prev,
                                                    RpmMax: parseRpmInput(event.target.value),
                                                }))
                                            }
                                        />
                                    </Form.Field>
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </Form>
                </Segment>
            )}

            {hasSearched && (
                <Message info size="small">
                    {totalCountLoaded ? (
                        <>
                            {totalCount.toLocaleString('pt-BR')} registro(s) encontrado(s)
                            {totalPages > 1 && ` — página ${currentPage} de ${totalPages}`}
                        </>
                    ) : (
                        <>
                            Mostrando página {currentPage}
                            {logs.length > 0 && ` (${logs.length} registro(s) nesta página)`}
                            {' — '}
                            <Button basic compact size="mini" onClick={handleLoadTotal} loading={loading} disabled={loading || isResultStale}>
                                Carregar total
                            </Button>
                        </>
                    )}
                </Message>
            )}

            <Message warning size="small">
                <Message.Header>Sobre carga no motor e premiação</Message.Header>
                <p>
                    O campo <strong>Carga no motor</strong> (Sim/Não) é calculado no salvamento do log quando{' '}
                    <strong>engine load &gt; 90%</strong>.
                </p>
                <p>
                    No cálculo de <strong>premiação</strong>, porém, considera-se carga no motor quando{' '}
                    <strong>RPM &gt; 1500</strong> e o <strong>freio motor está desligado</strong> — independentemente
                    do booleano salvo no log.
                </p>
                <p>Use a coluna <strong>RPM</strong> para auditar registros alinhados à regra de premiação.</p>
            </Message>

            <Table color="green" striped>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell textAlign="right">#</Table.HeaderCell>
                        <Table.HeaderCell>Placa</Table.HeaderCell>
                        <Table.HeaderCell>Motorista</Table.HeaderCell>
                        <Table.HeaderCell>RPM</Table.HeaderCell>
                        <Table.HeaderCell>Carga no motor</Table.HeaderCell>
                        <Table.HeaderCell>Carga motor (%)</Table.HeaderCell>
                        <Table.HeaderCell>Acelerador</Table.HeaderCell>
                        <Table.HeaderCell>Odômetro</Table.HeaderCell>
                        <Table.HeaderCell>Cliente</Table.HeaderCell>
                        <Table.HeaderCell>Data/Hora</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {logs?.length === 0 && hasSearched && !loading && (
                        <Table.Row>
                            <Table.Cell colSpan={10} textAlign="center">
                                Nenhum log encontrado para os filtros informados.
                            </Table.Cell>
                        </Table.Row>
                    )}
                    {logs?.map((log, index) => (
                        <Table.Row key={log.id}>
                            <Table.Cell disabled textAlign="right">#{rowOffset + index + 1}</Table.Cell>
                            <Table.Cell>{StringUtils.formatarPlaca(log.placa) ?? ''}</Table.Cell>
                            <Table.Cell>
                                {log.motoristaNome
                                    ? `${log.motoristaNome} | ${StringUtils.formatCPF(log.motoristaCpf)}`
                                    : '-------'}
                            </Table.Cell>
                            <Table.Cell>{log.rpm != null ? log.rpm.toLocaleString('pt-BR') : '-'}</Table.Cell>
                            <Table.Cell>{formatCargaNoMotor(log.cargaNoMotor)}</Table.Cell>
                            <Table.Cell>{log.engineLoad != null ? `${log.engineLoad}%` : '-'}</Table.Cell>
                            <Table.Cell>{log.posicaoAcelerador != null ? `${log.posicaoAcelerador}%` : '-'}</Table.Cell>
                            <Table.Cell>
                                {log.odometro
                                    ? `${StringUtils.toBrazilianDecimalFormat(log.odometro / 1000)} km`
                                    : '-'}
                            </Table.Cell>
                            <Table.Cell>{log.clienteNome?.trim() ? log.clienteNome : '-'}</Table.Cell>
                            <Table.Cell>
                                {StringUtils.formatToBrazilianShortDateTime(log.savedAt) ?? ''}
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>

            {(totalPages > 1 || hasNextPage || hasPreviousPage) && !isResultStale && (
                totalCountLoaded ? (
                    <Pagination
                        totalPages={totalPages}
                        activePage={currentPage}
                        onPageChange={(_e, {activePage}) => {
                            setFilter(prev => ({...prev, Page: activePage as number}));
                        }}
                    />
                ) : (
                    <Button.Group>
                        <Button
                            icon="left chevron"
                            content="Anterior"
                            disabled={!hasPreviousPage || loading}
                            onClick={() => setFilter(prev => ({...prev, Page: Math.max(1, currentPage - 1)}))}
                        />
                        <Button
                            icon="right chevron"
                            iconPosition="right"
                            content="Próxima"
                            disabled={!hasNextPage || loading}
                            onClick={() => setFilter(prev => ({...prev, Page: currentPage + 1}))}
                        />
                    </Button.Group>
                )
            )}
        </div>
    );
};

export default RastreamentoLogsListPage;
